/**
 * Constantes compartilhadas para ativos físicos e outras categorias.
 * Estas constantes são sincronizadas com o backend para manter consistência.
 */

// Categorias válidas
export const ASSET_CATEGORIES = {
  physical: {
    label: 'Físicos',
    icon: 'computer',
    color: 'primary',
    description: 'Equipamentos físicos como computadores, monitores, impressoras'
  },
  token: {
    label: 'Tokens',
    icon: 'key',
    color: 'secondary',
    description: 'Tokens, credenciais e contexto de API'
  },
  license: {
    label: 'Licenças',
    icon: 'assignment',
    color: 'warning',
    description: 'Licenças de software e certificados'
  }
};

// Subcategorias físicas válidas (sincronizado com backend)
export const PHYSICAL_SUBCATEGORIES = {
  'Computadores': {
    label: 'Computadores',
    icon: 'computer',
    description: 'Desktops, laptops, workstations',
    required_fields: ['computer_type', 'manufacturer'],
    optional_fields: ['cpu', 'storage', 'memory', 'model', 'serial_number', 'inventory_number', 'network', 'uuid', 'invoice_link'],
    computer_types: ['Desktop', 'Laptop', 'Servidor', 'Workstation', 'All-in-One'],
    manufacturers: ['Dell', 'HP', 'Lenovo', 'Apple', 'Asus', 'Acer', 'Samsung', 'Outros']
  },
  'Monitores': {
    label: 'Monitores',
    icon: 'monitor',
    description: 'Monitores e displays',
    required_fields: ['monitor_type', 'size'],
    optional_fields: ['manufacturer', 'model', 'serial_number', 'inventory_number', 'management_type', 'invoice_link'],
    monitor_types: ['LED', 'LCD', 'OLED', 'CRT', 'Curvo', 'UltraWide', '4K'],
    manufacturers: ['Dell', 'HP', 'LG', 'Samsung', 'AOC', 'BenQ', 'ASUS', 'Outros']
  },
  'Impressoras': {
    label: 'Impressoras',
    icon: 'printer',
    description: 'Impressoras e multifuncionais',
    required_fields: ['printer_type'],
    optional_fields: ['manufacturer', 'model', 'serial_number', 'inventory_number', 'snmp_credential', 'memory', 'initial_page_counter', 'current_page_counter', 'invoice_link'],
    printer_types: ['Laser', 'Jato de Tinta', 'Multifuncional', 'Térmica', 'Matricial', '3D'],
    manufacturers: ['HP', 'Epson', 'Canon', 'Brother', 'Samsung', 'Lexmark', 'Outros']
  },
  'Periféricos': {
    label: 'Periféricos',
    icon: 'devices',
    description: 'Mouse, teclado, webcam e outros dispositivos',
    required_fields: ['devices_type'],
    optional_fields: ['manufacturer', 'model', 'serial_number', 'inventory_number', 'brand', 'invoice_link'],
    device_types: ['Mouse', 'Teclado', 'Webcam', 'Microfone', 'Caixa de Som', 'Headset', 'Scanner', 'Leitor de Código', 'HD Externo', 'Pendrive'],
    manufacturers: ['Logitech', 'Microsoft', 'Razer', 'Corsair', 'SteelSeries', 'HyperX', 'Multilaser', 'Outros']
  },
  'Servidores': {
    label: 'Servidores',
    icon: 'server',
    description: 'Servidores físicos e equipamentos de infraestrutura',
    required_fields: ['server_type'],
    optional_fields: ['cpu', 'storage', 'memory', 'manufacturer', 'model', 'serial_number', 'inventory_number', 'network', 'invoice_link'],
    server_types: ['Rack', 'Tower', 'Blade', 'Storage', 'Network', 'Cloud Appliance']
  },
  'Consumíveis': {
    label: 'Consumíveis',
    icon: 'inventory',
    description: 'Toner, cartuchos e outros materiais consumíveis',
    required_fields: ['manufacturer'],
    optional_fields: ['model', 'inventory_number', 'invoice_link'],
    consumable_types: ['Toner', 'Cartucho', 'Fita', 'Papel', 'Bateria', 'Outros']
  },
  'Telefonia': {
    label: 'Telefonia',
    icon: 'phone',
    description: 'Chips e linhas telefônicas',
    required_fields: ['phone_number'],
    optional_fields: [
      'phone_code', 'phone_situation', 'phone_user', 'carrier', 'phone_plan',
      'phone_add_on', 'iccid', 'service_start_date', 'fidelity_date',
      'line_value', 'mfa_code', 'recovery_email', 'usage_term',
      'usage_start_date', 'usage_end_date', 'return_term', 'invoice_link'
    ],
    carriers: ['Claro', 'Tim', 'Vivo', 'Oi', 'Algar', 'Outros']
  },
  'Outro': {
    label: 'Outro',
    icon: 'device_unknown',
    description: 'Outros tipos de equipamentos físicos',
    required_fields: [],
    optional_fields: ['manufacturer', 'model', 'serial_number', 'inventory_number', 'invoice_link']
  }
};

// Status válidos
export const ASSET_STATUSES = {
  active: {
    label: 'Ativo',
    color: 'success',
    description: 'Equipamento em uso normal'
  },
  inactive: {
    label: 'Inativo',
    color: 'default',
    description: 'Equipamento fora de uso'
  },
  maintenance: {
    label: 'Manutenção',
    color: 'warning',
    description: 'Equipamento em manutenção'
  }
};



// Tipos de gerenciamento para monitores
export const MANAGEMENT_TYPES = [
  'Unit management',
  'Global management'
];

// Funções auxiliares
export const getCategoryInfo = (category) => {
  return ASSET_CATEGORIES[category] || {};
};

export const getSubcategoryInfo = (subcategory) => {
  return PHYSICAL_SUBCATEGORIES[subcategory] || {};
};

export const getStatusInfo = (status) => {
  return ASSET_STATUSES[status] || {};
};

export const getFieldOptions = (fieldName, subcategory = null) => {
  if (subcategory && PHYSICAL_SUBCATEGORIES[subcategory]) {
    const subcatInfo = PHYSICAL_SUBCATEGORIES[subcategory];

    // Mapeia nomes de campos para listas de opções
    const fieldOptions = {
      computer_type: subcatInfo.computer_types || [],
      monitor_type: subcatInfo.monitor_types || [],
      printer_type: subcatInfo.printer_types || [],
      devices_type: subcatInfo.device_types || [],
      server_type: subcatInfo.server_types || [],
      consumable_type: subcatInfo.consumable_types || [],
      manufacturer: subcatInfo.manufacturers || [],
      management_type: MANAGEMENT_TYPES
    };

    return fieldOptions[fieldName] || [];
  }

  return [];
};

export const isFieldRequired = (fieldName, subcategory = null) => {
  if (subcategory && PHYSICAL_SUBCATEGORIES[subcategory]) {
    return PHYSICAL_SUBCATEGORIES[subcategory].required_fields.includes(fieldName);
  }
  return false;
};

export const isFieldOptional = (fieldName, subcategory = null) => {
  if (subcategory && PHYSICAL_SUBCATEGORIES[subcategory]) {
    const subcatInfo = PHYSICAL_SUBCATEGORIES[subcategory];
    return !subcatInfo.required_fields.includes(fieldName) &&
           ['model', 'serial_number', 'inventory_number', 'network', 'uuid'].includes(fieldName);
  }
  return false;
};

export const getAllFieldsForSubcategory = (subcategory) => {
  if (subcategory && PHYSICAL_SUBCATEGORIES[subcategory]) {
    const subcatInfo = PHYSICAL_SUBCATEGORIES[subcategory];
    return [...subcatInfo.required_fields, 'model', 'serial_number', 'inventory_number', 'network', 'uuid'];
  }
  return [];
};

// Validações específicas (sincronizadas com o backend)
export const validateAssetData = (data, isUpdate = false) => {
  const errors = [];

  // Validação básica obrigatória
  if (!data.name || !data.name.trim()) {
    errors.push('Nome do ativo é obrigatório');
  }

  if (!data.category) {
    errors.push('Categoria é obrigatória');
  }

  // Validações específicas por categoria
  const category = data.category;

  if (category === 'physical') {
    const physicalErrors = validatePhysicalAssetData(data, isUpdate);
    errors.push(...physicalErrors);
  } else if (category === 'token') {
    const tokenErrors = validateTokenAssetData(data, isUpdate);
    errors.push(...tokenErrors);
  } else if (category === 'license') {
    const licenseErrors = validateLicenseAssetData(data, isUpdate);
    errors.push(...licenseErrors);
  }

  return errors.length === 0 ? { isValid: true } : { isValid: false, errors };
};

export const validatePhysicalAssetData = (data, isUpdate = false) => {
  const errors = [];

  // Subcategoria é obrigatória para ativos físicos
  const subcategory = data.subcategory;
  if (!subcategory) {
    errors.push('Subcategoria é obrigatória para ativos físicos');
  } else if (!PHYSICAL_SUBCATEGORIES[subcategory]) {
    errors.push('Subcategoria inválida');
  }

  if (!subcategory || !PHYSICAL_SUBCATEGORIES[subcategory]) {
    return errors;
  }

  const subcategoryInfo = PHYSICAL_SUBCATEGORIES[subcategory];

  // Validações específicas por subcategoria
  if (subcategory === 'Computadores') {
    if (!isUpdate || data.computer_type) {
      if (!data.computer_type) {
        errors.push('Tipo de computador é obrigatório');
      }
    }

    if (!isUpdate || data.manufacturer) {
      if (!data.manufacturer) {
        errors.push('Fabricante é obrigatório para computadores');
      }
    }
  } else if (subcategory === 'Monitores') {
    if (!isUpdate || data.monitor_type) {
      if (!data.monitor_type) {
        errors.push('Tipo de monitor é obrigatório');
      }
    }

    // Validação de tamanho se fornecido
    const size = data.size;
    if (size !== undefined && size !== '') {
      const sizeFloat = parseFloat(size);
      if (isNaN(sizeFloat) || sizeFloat <= 0) {
        errors.push('Tamanho deve ser maior que zero');
      } else if (sizeFloat > 100) {
        errors.push('Tamanho parece muito grande (máximo esperado: 100 polegadas)');
      }
    }
  } else if (subcategory === 'Impressoras') {
    if (!isUpdate || data.printer_type) {
      if (!data.printer_type) {
        errors.push('Tipo de impressora é obrigatório');
      }
    }

    // Validação de contadores se fornecidos
    const initialCounter = data.initial_page_counter;
    if (initialCounter !== undefined && initialCounter !== '') {
      const initialInt = parseInt(initialCounter, 10);
      if (isNaN(initialInt) || initialInt < 0) {
        errors.push('Contador inicial não pode ser negativo');
      }
    }

    const currentCounter = data.current_page_counter;
    if (currentCounter !== undefined && currentCounter !== '') {
      const currentInt = parseInt(currentCounter, 10);
      if (isNaN(currentInt) || currentInt < 0) {
        errors.push('Contador atual não pode ser negativo');
      }
    }
  } else if (subcategory === 'Periféricos') {
    if (!isUpdate || data.devices_type) {
      if (!data.devices_type) {
        errors.push('Tipo de dispositivo é obrigatório');
      }
    }
  } else if (subcategory === 'Servidores') {
    if (!isUpdate || data.server_type) {
      if (!data.server_type) {
        errors.push('Tipo de servidor é obrigatório');
      }
    }
  } else if (subcategory === 'Telefonia') {
    if (!isUpdate || data.phone_number) {
      if (!data.phone_number) {
        errors.push('Número é obrigatório para Telefonia');
      }
    }
  }

  return errors;
};

export const validateTokenAssetData = (data, isUpdate = false) => {
  const errors = [];

  // Token value é obrigatório para tokens
  if (!isUpdate || data.token_value) {
    if (!data.token_value) {
      errors.push('Valor do token é obrigatório');
    }
  }

  return errors;
};

export const validateApiAssetData = () => [
  'Cadastro de APIs foi descontinuado. Use Tokens para registrar credenciais e contexto de API.'
];

export const validateLicenseAssetData = (data, isUpdate = false) => {
  const errors = [];

  // License key é obrigatória
  if (!isUpdate || data.license_key) {
    if (!data.license_key) {
      errors.push('Chave da licença é obrigatória');
    }
  }

  // Data de expiração deve ser futura se fornecida
  const expiryDate = data.expiry_date;
  if (expiryDate) {
    try {
      const expiry = new Date(expiryDate);
      if (expiry <= new Date()) {
        errors.push('Data de expiração deve ser futura');
      }
    } catch (error) {
      errors.push('Data de expiração inválida');
    }
  }

  return errors;
};

// Função para sanitizar dados
export const sanitizeAssetData = (data) => {
  const sanitized = {};

  Object.entries(data).forEach(([key, value]) => {
    // Remove campos vazios, null ou apenas espaços
    if (value !== null && value !== '' && !(typeof value === 'string' && !value.trim())) {
      // Converte valores numéricos
      if (['value', 'size'].includes(key) && value !== '') {
        const numValue = parseFloat(value);
        sanitized[key] = isNaN(numValue) ? value : numValue;
      } else if (['memory', 'initial_page_counter', 'current_page_counter'].includes(key) && value !== '') {
        const numValue = parseInt(value, 10);
        sanitized[key] = isNaN(numValue) ? value : numValue;
      } else {
        sanitized[key] = value;
      }
    }
  });

  return sanitized;
};

// Mapa para compatibilidade de subcategorias antigas -> novas
export const SUBCATEGORY_ALIASES = {
  'Computador': 'Computadores',
  'Monitor': 'Monitores',
  'Impressora': 'Impressoras',
  'Periférico': 'Periféricos',
  'Perifericos': 'Periféricos',
  'Consumiveis': 'Consumíveis'
};

// Função para normalizar subcategorias
export const normalizeSubcategory = (subcategory) => {
  return SUBCATEGORY_ALIASES[subcategory] || subcategory;
};
